import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Visibility as LogsIcon
} from '@mui/icons-material';
import axios from 'axios';
import { cronToHumanReadable } from '../../utils/cronUtils';

export default function BrandPipelinesSection({ onNotification }) {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [editForm, setEditForm] = useState({
    cron_expression: '',
    sync_window_days: 7
  });
  
  // Custom run dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runningPipeline, setRunningPipeline] = useState(null);
  const [customDates, setCustomDates] = useState({
    start_date: '',
    end_date: ''
  });
  
  // Logs dialog state
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedJobLogs, setSelectedJobLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Load brand pipelines
  const loadPipelines = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/brands/pipelines');
      setPipelines(response.data.pipelines || []);
      setError(null);
    } catch (err) {
      console.error('Error loading brand pipelines:', err);
      setError('Failed to load brand pipelines');
      if (onNotification) {
        onNotification('Failed to load brand pipelines', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPipelines();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadPipelines, 60000);
    return () => clearInterval(interval);
  }, []);

  // Toggle pipeline enabled/disabled
  const handleTogglePipeline = async (brandId, pipelineName, currentStatus) => {
    try {
      await axios.post(`/api/brands/${brandId}/pipelines/${pipelineName}/toggle`, {
        is_enabled: !currentStatus
      });
      
      if (onNotification) {
        onNotification(
          `Pipeline ${currentStatus ? 'disabled' : 'enabled'} successfully`,
          'success'
        );
      }
      
      loadPipelines();
    } catch (err) {
      console.error('Error toggling pipeline:', err);
      if (onNotification) {
        onNotification('Failed to toggle pipeline', 'error');
      }
    }
  };

  // Manual trigger pipeline (quick run with default dates)
  const handleRunPipeline = async (brandId, pipelineName) => {
    try {
      const response = await axios.post(`/api/brands/${brandId}/pipelines/${pipelineName}/trigger`);
      
      if (onNotification) {
        onNotification(
          `Pipeline queued successfully! Job ID: ${response.data.jobId}`,
          'success'
        );
      }
    } catch (err) {
      console.error('Error triggering pipeline:', err);
      if (onNotification) {
        onNotification('Failed to trigger pipeline', 'error');
      }
    }
  };
  
  // Open edit dialog
  const handleEditPipeline = (pipeline) => {
    setEditingPipeline(pipeline);
    setEditForm({
      cron_expression: pipeline.cron_expression || '30 3 * * *',
      sync_window_days: pipeline.sync_window_days || 7
    });
    setEditDialogOpen(true);
  };
  
  // Save pipeline edits
  const handleSaveEdit = async () => {
    try {
      await axios.post(
        `/api/brands/${editingPipeline.brand_id}/pipelines/${editingPipeline.pipeline_name}/update`,
        editForm
      );
      
      if (onNotification) {
        onNotification('Pipeline updated successfully', 'success');
      }
      
      setEditDialogOpen(false);
      loadPipelines();
    } catch (err) {
      console.error('Error updating pipeline:', err);
      if (onNotification) {
        onNotification('Failed to update pipeline', 'error');
      }
    }
  };
  
  // Open custom run dialog
  const handleOpenCustomRun = (pipeline) => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    setRunningPipeline(pipeline);
    setCustomDates({
      start_date: thirtyDaysAgo.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    });
    setRunDialogOpen(true);
  };
  
  // Run pipeline with custom dates
  const handleCustomRun = async () => {
    try {
      const response = await axios.post(
        `/api/brands/${runningPipeline.brand_id}/pipelines/${runningPipeline.pipeline_name}/trigger`,
        customDates
      );
      
      if (onNotification) {
        onNotification(
          `Pipeline queued for ${customDates.start_date} to ${customDates.end_date}! Job ID: ${response.data.jobId}`,
          'success'
        );
      }
      
      setRunDialogOpen(false);
    } catch (err) {
      console.error('Error running custom pipeline:', err);
      if (onNotification) {
        onNotification('Failed to run pipeline', 'error');
      }
    }
  };
  
  // View job logs
  const handleViewLogs = async (brandId, pipelineName) => {
    setLoadingLogs(true);
    setLogsDialogOpen(true);
    
    try {
      // Get recent pipeline jobs
      const response = await axios.get(
        `/api/brands/${brandId}/pipelines/${pipelineName}/jobs?limit=10`
      );
      setSelectedJobLogs(response.data.jobs || []);
    } catch (err) {
      console.error('Error loading logs:', err);
      setSelectedJobLogs([]);
      if (onNotification) {
        onNotification('Failed to load job logs', 'error');
      }
    } finally {
      setLoadingLogs(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp || timestamp === '1970-01-01 00:00:00') return 'Never';
    
    const now = new Date();
    const time = new Date(timestamp.replace(' ', 'T') + 'Z');
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Format next run time
  const formatNextRun = (timestamp) => {
    if (!timestamp) return 'Not scheduled';
    
    const time = new Date(timestamp.replace(' ', 'T') + 'Z');
    const now = new Date();
    
    if (time <= now) return 'Due now';
    
    const diffMs = time - now;
    const diffMins = Math.ceil(diffMs / (1000 * 60));
    
    if (diffMins < 60) return `In ${diffMins}m`;
    const diffHours = Math.ceil(diffMins / 60);
    if (diffHours < 24) return `In ${diffHours}h`;
    
    return time.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get pipeline display name
  const getPipelineDisplayName = (name) => {
    const displayNames = {
      'customer_journey': 'Customer Journey',
      'shopify_transform': 'Shopify Transform',
      'amazon_transform': 'Amazon Transform'
    };
    return displayNames[name] || name;
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading brand pipelines...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (pipelines.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No brand pipelines configured. Pipelines are automatically created when you add a new brand.
      </Alert>
    );
  }

  return (
    <Paper sx={{ mt: 4 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="600">
          Brand Data Pipelines
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Automated data transformation pipelines that run daily for each brand
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell>Pipeline</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Next Run</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pipelines.map((pipeline) => (
              <TableRow key={`${pipeline.brand_id}-${pipeline.pipeline_name}`}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {pipeline.brand_name}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon fontSize="small" color="primary" />
                    <Typography variant="body2">
                      {getPipelineDisplayName(pipeline.pipeline_name)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {pipeline.sync_window_days}-day rolling window
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      {cronToHumanReadable(pipeline.cron_expression || '30 3 * * *')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '10px' }}>
                      {pipeline.cron_expression || '30 3 * * *'}
                    </Typography>
                    {pipeline.is_enabled ? (
                      <Chip label="Active" color="success" size="small" sx={{ mt: 0.5 }} />
                    ) : (
                      <Chip label="Paused" color="default" size="small" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatRelativeTime(pipeline.last_run)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNextRun(pipeline.next_run)}
                  </Typography>
                </TableCell>
                
                
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={pipeline.is_enabled ? "Pause Pipeline" : "Resume Pipeline"}>
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePipeline(
                          pipeline.brand_id,
                          pipeline.pipeline_name,
                          pipeline.is_enabled
                        )}
                      >
                        {pipeline.is_enabled ? (
                          <PauseIcon fontSize="small" />
                        ) : (
                          <RunIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Run Now">
                      <IconButton
                        size="small"
                        onClick={() => handleRunPipeline(pipeline.brand_id, pipeline.pipeline_name)}
                        color="primary"
                      >
                        <RunIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="View Logs">
                      <IconButton
                        size="small"
                        onClick={() => handleViewLogs(pipeline.brand_id, pipeline.pipeline_name)}
                        color="info"
                      >
                        <LogsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Edit Pipeline">
                      <IconButton
                        size="small"
                        onClick={() => handleEditPipeline(pipeline)}
                        color="secondary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Custom Date Run">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenCustomRun(pipeline)}
                        color="secondary"
                      >
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Edit Pipeline Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Pipeline Settings</DialogTitle>
        <DialogContent>
          {editingPipeline && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Brand:</strong> {editingPipeline.brand_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Pipeline:</strong> {getPipelineDisplayName(editingPipeline.pipeline_name)}
              </Typography>
              
              <TextField
                label="Cron Expression"
                value={editForm.cron_expression}
                onChange={(e) => setEditForm({ ...editForm, cron_expression: e.target.value })}
                fullWidth
                helperText="Format: minute hour day month weekday (e.g., '30 3 * * *' = 3:30 AM daily)"
              />
              
              <TextField
                label="Sync Window (Days)"
                type="number"
                value={editForm.sync_window_days}
                onChange={(e) => setEditForm({ ...editForm, sync_window_days: parseInt(e.target.value) })}
                fullWidth
                inputProps={{ min: 1, max: 365 }}
                helperText="Number of days of historical data to process"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Custom Run Dialog */}
      <Dialog open={runDialogOpen} onClose={() => setRunDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run Pipeline with Custom Dates</DialogTitle>
        <DialogContent>
          {runningPipeline && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Brand:</strong> {runningPipeline.brand_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Pipeline:</strong> {getPipelineDisplayName(runningPipeline.pipeline_name)}
              </Typography>
              
              <Alert severity="info" sx={{ mt: 1 }}>
                Select a custom date range for this one-time run. The pipeline will process data between these dates.
              </Alert>
              
              <TextField
                label="Start Date"
                type="date"
                value={customDates.start_date}
                onChange={(e) => setCustomDates({ ...customDates, start_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="End Date"
                type="date"
                value={customDates.end_date}
                onChange={(e) => setCustomDates({ ...customDates, end_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              
              <Typography variant="caption" color="text.secondary">
                Date range: {customDates.start_date} to {customDates.end_date}
                {customDates.start_date && customDates.end_date && (
                  <> ({Math.ceil((new Date(customDates.end_date) - new Date(customDates.start_date)) / (1000 * 60 * 60 * 24))} days)</>
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCustomRun} 
            variant="contained" 
            color="primary"
            disabled={!customDates.start_date || !customDates.end_date}
          >
            Run Pipeline
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Logs Viewer Dialog */}
      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pipeline Job History</DialogTitle>
        <DialogContent>
          {loadingLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedJobLogs && selectedJobLogs.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Records</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedJobLogs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {job.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          size="small"
                          color={
                            job.status === 'completed' ? 'success' :
                            job.status === 'failed' ? 'error' :
                            job.status === 'running' ? 'primary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {job.duration_minutes ? `${job.duration_minutes}m` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {job.records_processed || 0} / {job.records_failed || 0} failed
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No job history found for this pipeline.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
